import React from 'react';
import { T, sx } from '../lib/tokens';
import { Wordmark } from '../components/ui/Wordmark';
import { useIsMobile } from '../lib/useIsMobile';

export function Security() {
  const m = useIsMobile();
  return (
    <LegalShell title="Security" subtitle="Last updated 18 April 2026" m={m}>
      <p style={p}>
        We hold the keys to your Xero org. We take that responsibility seriously. Here's how
        the service is built and what we do to keep your data safe.
      </p>

      <h2 style={h2}>Infrastructure</h2>
      <p style={p}>
        receiptrec runs entirely on Cloudflare's edge network - Workers for compute, D1 for
        relational data, R2 for receipt files. There are no traditional servers to patch and
        no dedicated hosts sitting idle between requests.
      </p>

      <h2 style={h2}>Data at rest</h2>
      <p style={p}>
        Xero and Gmail OAuth tokens are encrypted with AES-256-GCM using a key derived from
        our JWT secret before they're written to the database. Receipt files live in R2,
        which is encrypted at rest by default.
      </p>

      <h2 style={h2}>Data in transit</h2>
      <p style={p}>
        All traffic to and from receiptrec is TLS 1.3. We set a Strict-Transport-Security
        header so browsers won't talk to us in plain HTTP, ever.
      </p>

      <h2 style={h2}>Authentication</h2>
      <p style={p}>
        Sign-in is handled by Xero OAuth2 - we never see your Xero password. Session tokens
        are HttpOnly SameSite cookies with a 15-minute access window and a rotating 30-day
        refresh token.
      </p>

      <h2 style={h2}>Isolation</h2>
      <p style={p}>
        Every database query is filtered by user_id. Every R2 object key is prefixed with
        the owning user's id. We regularly audit our routes for multi-tenant boundary
        violations, because that's the one class of bug we can't afford.
      </p>

      <h2 style={h2}>Backups</h2>
      <p style={p}>
        Cloudflare D1 Time Travel gives us 30-day point-in-time recovery for the database.
        R2 versioning is enabled on the receipt bucket, so accidental overwrites and
        deletions are recoverable.
      </p>

      <h2 style={h2}>Monitoring</h2>
      <p style={p}>
        We emit structured logs for every request, track errors with Sentry, and rely on
        Cloudflare's built-in anomaly detection for unusual traffic patterns.
      </p>

      <h2 style={h2}>Compliance</h2>
      <p style={p}>
        We operate under the Australian Privacy Principles. Payments are handled by Stripe,
        which is certified to PCI DSS Level 1 - card data never touches our servers.
      </p>

      <h2 style={h2}>Responsible disclosure</h2>
      <p style={p}>
        Found a vulnerability? Please email{' '}
        <a href="mailto:security@receiptrec.com" style={a}>security@receiptrec.com</a>. We'll
        acknowledge within 2 business days and work with you on a fix. We don't have a bug
        bounty yet, but we're happy to credit researchers publicly.
      </p>
    </LegalShell>
  );
}

const p: React.CSSProperties = { fontSize: 16, lineHeight: 1.6, color: T.graphite, margin: '0 0 18px' };
const h2: React.CSSProperties = { ...sx.serif, fontSize: 26, letterSpacing: '-0.015em', fontWeight: 400, margin: '36px 0 14px', color: T.graphite };
const a: React.CSSProperties = { color: T.sageDarker, textDecoration: 'underline' };

function LegalShell({ title, subtitle, m, children }: { title: string; subtitle?: string; m: boolean; children: React.ReactNode }) {
  return (
    <div style={{ background: T.paper, color: T.graphite, minHeight: '100vh' }}>
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: T.navy, borderBottom: `1px solid ${T.navyLine}` }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: m ? '14px 20px' : '18px 40px' }}>
          <a href="#/" style={{ textDecoration: 'none' }}><Wordmark size={m ? 20 : 24} /></a>
          <a href="#/" style={{ fontSize: 14, color: T.creamMuted, textDecoration: 'none' }}>Back to home</a>
        </div>
      </header>
      <main style={{ maxWidth: 720, margin: '0 auto', padding: m ? '32px 20px' : '60px 40px' }}>
        <h1 style={{ ...sx.serif, fontSize: m ? 32 : 48, lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 400, margin: '0 0 10px' }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 13, color: T.graphiteDim, marginBottom: 28 }}>{subtitle}</div>}
        {children}
      </main>
    </div>
  );
}
